import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, IconButton, Menu, MenuItem, Paper, Snackbar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  Tooltip, Typography,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, MoreVert as MoreIcon,
} from '@mui/icons-material';
import api from '../utils/api';
import useAuthStore from '../store/authStore';

const EMPTY_FORM = { username: '', email: '', full_name: '', password: '', is_superuser: false };

// Backend `detail` is a string, or a list of strings/objects (422 password rules).
function errorMessage(err) {
  const detail = err?.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => (typeof d === 'string' ? d : d?.msg || JSON.stringify(d))).join(' ');
  }
  return detail || err?.message || 'Something went wrong';
}

export default function UsersPage() {
  const currentUser = useAuthStore((s) => s.user);
  const isAdmin = !!currentUser?.is_superuser;

  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [snack, setSnack] = useState(null);
  const [form, setForm] = useState(null); // {id?, ...fields}
  const [menu, setMenu] = useState(null); // {anchor, user}
  const [confirm, setConfirm] = useState(null); // user

  const load = useCallback(async () => {
    try {
      const res = await api.get('/users');
      setUsers(res.data || []);
    } catch (err) {
      setSnack(errorMessage(err));
    }
  }, []);

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      [u.username, u.full_name, u.email].some((v) => (v || '').toLowerCase().includes(q)));
  }, [users, search]);

  if (!isAdmin) {
    return <Typography variant="h6" sx={{ p: 3 }}>Admins only</Typography>;
  }

  const isSelf = (u) => u.id === currentUser?.id;

  const save = async () => {
    try {
      if (form.id) {
        const body = {
          username: form.username,
          email: form.email,
          full_name: form.full_name,
          is_superuser: form.is_superuser,
        };
        if (form.password) body.password = form.password;
        await api.put(`/users/${form.id}`, body);
      } else {
        await api.post('/users', {
          username: form.username,
          email: form.email,
          full_name: form.full_name,
          password: form.password,
          is_superuser: form.is_superuser,
        });
      }
      setForm(null);
      load();
    } catch (err) {
      setSnack(errorMessage(err));
    }
  };

  const toggleActive = async () => {
    const u = confirm;
    try {
      await api.put(`/users/${u.id}`, { is_active: !u.is_active });
      setConfirm(null);
      load();
    } catch (err) {
      setConfirm(null);
      setSnack(errorMessage(err));
    }
  };

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const editing = !!form?.id;
  const self = editing && form.id === currentUser?.id;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>Users</Typography>
        <TextField
          size="small" placeholder="Search users" value={search}
          onChange={(e) => setSearch(e.target.value)}
          inputProps={{ 'aria-label': 'Search users' }}
        />
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setForm({ ...EMPTY_FORM })}>
          Add user
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Username</TableCell>
              <TableCell>Full name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((u) => (
              <TableRow key={u.id} hover>
                <TableCell>{u.username}</TableCell>
                <TableCell>{u.full_name}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>
                  <Chip size="small" label={u.is_superuser ? 'Admin' : 'User'}
                    color={u.is_superuser ? 'primary' : 'default'} />
                </TableCell>
                <TableCell>
                  <Chip size="small" label={u.is_active ? 'Active' : 'Deactivated'}
                    color={u.is_active ? 'success' : 'default'} />
                </TableCell>
                <TableCell>{u.created_at ? new Date(u.created_at).toLocaleDateString() : ''}</TableCell>
                <TableCell align="right">
                  <IconButton aria-label="Edit" size="small"
                    onClick={() => setForm({ ...u, password: '' })}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton aria-label="More actions" size="small"
                    onClick={(e) => setMenu({ anchor: e.currentTarget, user: u })}>
                    <MoreIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu anchorEl={menu?.anchor} open={!!menu} onClose={() => setMenu(null)}>
        {menu && (
          <Tooltip title={isSelf(menu.user) ? "You can't deactivate your own account" : ''}>
            <span>
              <MenuItem
                disabled={isSelf(menu.user)}
                onClick={() => { setConfirm(menu.user); setMenu(null); }}
              >
                {menu.user.is_active ? 'Deactivate' : 'Reactivate'}
              </MenuItem>
            </span>
          </Tooltip>
        )}
      </Menu>

      <Dialog open={!!form} onClose={() => setForm(null)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? 'Edit user' : 'Add user'}</DialogTitle>
        {form && (
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
            <TextField label="Username" value={form.username} onChange={setField('username')} required />
            <TextField label="Email" type="email" value={form.email || ''} onChange={setField('email')} required />
            <TextField label="Full name" value={form.full_name || ''} onChange={setField('full_name')} />
            <TextField
              label={editing ? 'Reset password' : 'Password'} type="password"
              value={form.password} onChange={setField('password')}
              helperText={editing ? 'Leave empty to keep the current password' : undefined}
              required={!editing} autoComplete="new-password"
            />
            <Tooltip title={self ? "You can't remove your own admin role" : ''}>
              <span>
                <FormControlLabel
                  control={
                    <Checkbox checked={!!form.is_superuser} disabled={self}
                      onChange={(e) => setForm({ ...form, is_superuser: e.target.checked })} />
                  }
                  label="Administrator"
                />
              </span>
            </Tooltip>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setForm(null)}>Cancel</Button>
          <Button variant="contained" onClick={save}>{editing ? 'Save' : 'Create'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirm} onClose={() => setConfirm(null)}>
        <DialogTitle>{confirm?.is_active ? 'Deactivate user' : 'Reactivate user'}</DialogTitle>
        <DialogContent>
          <Typography>
            {confirm?.is_active
              ? `${confirm?.username} will no longer be able to sign in.`
              : `${confirm?.username} will be able to sign in again.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button variant="contained" onClick={toggleActive}>Confirm</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={6000} onClose={() => setSnack(null)}>
        <Alert severity="error" onClose={() => setSnack(null)}>{snack}</Alert>
      </Snackbar>
    </Box>
  );
}
